import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { withAuthenticatedUser } from 'auth/helpers';
import { Routes } from 'navigation/types';

import { ErrorPage, UploadZone, UploadZonePositioningWrapper } from '@izimi/components';
import { appEnv, i18nNamespaceLoader } from '@izimi/core';
import Layout from 'common/components/Layout/Layout';

const LoginRoute = lazy(() => import('../pages/login'));
const VaultRoute = lazy(() => import('../pages/vault'));
const VaultPreviewRoute = lazy(() => import('../pages/vault/preview'));
const VaultPendingRoute = lazy(() => import('../pages/vault/pending'));
const VaultTransferRoute = lazy(() => import('../pages/vault-transfer'));
const DossiersRoute = lazy(() => import('../pages/dossiers'));
const DossierRoute = lazy(() => import('../pages/dossiers/dossier'));
const DossierPreviewRoute = lazy(() => import('../pages/dossiers/dossier/preview'));
const ContactsRoute = lazy(() => import('../pages/contacts'));
const ProfileRoute = lazy(() => import('../pages/profile'));
const ProfileMailNotificationsRoute = lazy(() => import('../pages/profile/emailNotifications'));
const HistoryLogRoute = lazy(() => import('../pages/history-log'));
const TrashRoute = lazy(() => import('../pages/trash'));

const HelpInfoRoute = lazy(() => import('../pages/help/info'));
const HelpReportProblemRoute = lazy(() => import('../pages/help/report-problem'));

const CertificationRoute = lazy(() => import('../pages/certification'));

const DebugRoute = lazy(() => import('../pages/debug'));

const AuthenticatedLayout = withAuthenticatedUser(Layout);
const AuthenticatedUploadZonePositioningWrapper = withAuthenticatedUser(() => (
  <UploadZonePositioningWrapper>
    <UploadZone />
  </UploadZonePositioningWrapper>
));

const AuthenticatedRoutesWrapper = () => (
  <Suspense fallback={<AuthenticatedLayout />}>
    <Outlet />
    <AuthenticatedUploadZonePositioningWrapper>
      <UploadZone />
    </AuthenticatedUploadZonePositioningWrapper>
  </Suspense>
);

export const routes: RouteObject[] = [
  {
    path: Routes.Login,
    element: <LoginRoute />,
  },
  {
    element: <AuthenticatedRoutesWrapper />,
    children: [
      { path: Routes.Vault, element: <VaultRoute /> },
      { path: Routes.VaultPreview, element: <VaultPreviewRoute /> },
      {
        path: Routes.VaultPending,
        element: <VaultPendingRoute />,
        loader: i18nNamespaceLoader('sharing'),
      },
      { path: Routes.VaultTransfer, element: <VaultTransferRoute /> },
      {
        path: Routes.Dossiers,
        element: <DossiersRoute />,
        loader: i18nNamespaceLoader(['dossier', 'communications']),
      },
      {
        path: Routes.Dossier,
        element: <DossierRoute />,
        loader: i18nNamespaceLoader(['dossier', 'communications']),
      },
      {
        path: Routes.DossierPreview,
        element: <DossierPreviewRoute />,
        loader: i18nNamespaceLoader(['dossier', 'communications']),
      },
      { path: Routes.Contacts, element: <ContactsRoute /> },
      { path: Routes.Profile, element: <ProfileRoute /> },
      { path: Routes.ProfileMailNotifications, element: <ProfileMailNotificationsRoute /> },
      { path: Routes.HistoryLog, element: <HistoryLogRoute /> },
      { path: Routes.Trash, element: <TrashRoute /> },
    ],
  },
  {
    path: Routes.Certification,
    element: <CertificationRoute />,
  },
  {
    path: Routes.HelpInfo,
    element: <HelpInfoRoute />,
  },
  {
    path: Routes.HelpReportProblem,
    element: <HelpReportProblemRoute />,
  },
  ...(appEnv !== 'production' ? [{ path: Routes.Debug, element: <DebugRoute /> }] : []),
  {
    path: '*',
    element: <ErrorPage statusCode={404} />,
  },
];
